import { ReactElement, ReactNode } from 'react';

import { TAnchor } from 'utils/itineraries/functions/utils';
import { IPage, IProposal } from 'utils/itineraryDetails/types';

import {
  TAddBlockHandlerFn,
  TAddBlockInsideSpoilerFn,
  TDeleteBlockHandlerFn,
  TDeleteBlockInsideSpoilerFn,
  TUpdateBlockHandlerFn,
  TUpdateBlockInsideSpoilerFn,
} from './Page/RightSide/Block/types';
import { EPageLayout, EPageOrder, EPageRole, EPageType } from './Page/types';

export interface ILandingConfig {
  header: false | ILandingHeaderConfig;
  pages: TLandingPagesConfig;
  // whether enabled Guest Form for GP or not
  guestFormDisabled: boolean;
  css: string;
  map: IMapConfig;
  settings?: ILandingSettings;
}

export type ILandingSettings = Partial<{
  showInfoEventsInTravelBrief: boolean;
  textOverImages: boolean;
  displayDatesDdMmYyyy: boolean;
  displayTimes24h: boolean;
  language: string;
}>;

export interface IMapConfig {
  type: EMapType;
  styles?: string;
}

export enum EMapType {
  Satellite = 'satellite',
  Hybrid = 'hybrid',
  Roadmap = 'roadmap',
  Terrain = 'terrain',
}

export enum ELandingHeaderLayout {
  HalfWidth = 'halfWidth',
  FullWidth = 'fullWidth',
}

export interface ILandingHeaderConfig {
  layout: ELandingHeaderLayout;
  navBar: false | INavBarConfig;
  topBar: false | ITopBarConfig;
}

export interface INavBarConfig {
  mobile: boolean;
  desktop: boolean;
}

export interface ITopBarConfig {
  leftActions: boolean;
}

export interface ILandingV2Props {
  config?: ILandingConfig;
  data: IProposal;
  readonly?: boolean;
  onSetActivePageId?: (pageId: number) => void;
  activePageId?: number;
  // used for initial scroll position for landing
  defaultAnchor?: TAnchor;
  scrollPosition?: {
    pageId?: number;
    blockId?: number;
  };
  deviceType?: ELandingDeviceType;
  rightActions?: ReactNode;
  leftActions?: ReactNode;
  pageHandlersProvider?: TPageHandlersProviderFn;
  blockHandlersProvider?: TBlockHandlersProviderFn;
}

export enum ELandingDeviceType {
  Desktop = 'desktop',
  Mobile = 'mobile',
}

export type TPageHandlersProviderFn = (props: {
  pageIndex: number;
  children(props: { changeCoverImage?: () => void; changeContent?: (data: Partial<IPage>) => void }): ReactElement;
}) => ReactElement;

export type TBlockHandlersProviderFn = (props: {
  index: number[];
  children(props: {
    onAdd?: TAddBlockHandlerFn;
    onUpdate?: TUpdateBlockHandlerFn;
    onDelete?: TDeleteBlockHandlerFn;
    onAddBlockInsideSpoiler?: TAddBlockInsideSpoilerFn;
    onUpdateBlockInsideSpoiler?: TUpdateBlockInsideSpoilerFn;
    onDeleteBlockInsideSpoiler?: TDeleteBlockInsideSpoilerFn;
  }): ReactElement;
}) => ReactElement;

export type TLandingPagesConfig = ILandingPagesConfig[];

export interface ILandingPagesConfig {
  layout: EPageLayout;
  roles?: EPageRole[];
  types?: EPageType[];
  order?: EPageOrder[];
}
